import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { telasApi } from '../api/telas'
import { TelaResponse } from '../types/api'
import TelaOrdenacaoList from '../components/Telas/TelaOrdenacaoList'
import RefreshButton from '../components/RefreshButton'
import ActionBar from '../components/ActionBar'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const TelasPageEmpresa: React.FC = () => {
  const queryClient = useQueryClient()
  const [tabValue, setTabValue] = useState(0)
  const [tipoSistema, setTipoSistema] = useState<'ADMIN' | 'GALATEA_WEB' | 'all'>('all')
  const [empresaId, setEmpresaId] = useState<number | null>(null)

  // Buscar lista de telas da empresa/grupo específica
  const { data: telasEmpresa = [], isLoading: isLoadingTelas } = useQuery<TelaResponse[]>({
    queryKey: ['telas', 'empresa', empresaId],
    queryFn: () => telasApi.list({ 
      empresa_id: empresaId || undefined,
      incluir_inativos: false 
    }),
    enabled: empresaId !== null,
  })

  // Mutation para reordenar telas da empresa (apenas específicas)
  const reordenarMutation = useMutation({
    mutationFn: async (telas: Array<{ id: number; posicao_menu: number }>) => {
      return telasApi.reordenar(telas, false) // apenasGlobais = false
    },
    onSuccess: () => {
      // Invalidar queries sem refetch automático para evitar requisições incorretas
      queryClient.invalidateQueries({ 
        queryKey: ['telas', 'empresa', empresaId],
        exact: true,
        refetchType: 'none'
      })
      queryClient.invalidateQueries({ 
        queryKey: ['menu'],
        exact: false,
        refetchType: 'none'
      })
      // Refetch manual apenas da query específica
      queryClient.refetchQueries({ 
        queryKey: ['telas', 'empresa', empresaId],
        exact: true,
        type: 'active'
      })
    },
  })

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <ActionBar>
        <RefreshButton
          onClick={() => queryClient.invalidateQueries({ queryKey: ['telas'] })}
          disabled={isLoadingTelas}
          title="Atualizar"
        />
      </ActionBar>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" component="div">
          <strong>Gerenciamento de Telas por Empresa/Grupo</strong>
          <br />
          Aqui você pode gerenciar telas específicas da sua empresa ou grupo.
          <br />
          <strong>Nota:</strong> Apenas administradores de empresa/grupo podem acessar esta página.
          <br />
          Telas globais (padrão) são gerenciadas pelo Super Administrador.
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Empresa</InputLabel>
            <Select
              value={empresaId || ''}
              label="Empresa"
              onChange={(e) => setEmpresaId(e.target.value as number || null)}
            >
              <MenuItem value="">
                <em>Selecione uma empresa</em>
              </MenuItem>
              {/* TODO: Buscar empresas do usuário */}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Tipo Sistema</InputLabel>
            <Select
              value={tipoSistema}
              label="Tipo Sistema"
              onChange={(e) => setTipoSistema(e.target.value as 'ADMIN' | 'GALATEA_WEB' | 'all')}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="ADMIN">ADMIN</MenuItem>
              <MenuItem value="GALATEA_WEB">GALATEA_WEB</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Ordenação de Telas" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {isLoadingTelas ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : !empresaId ? (
            <Alert severity="warning" sx={{ m: 2 }}>
              Selecione uma empresa para gerenciar suas telas
            </Alert>
          ) : telasEmpresa.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Nenhuma tela específica encontrada para esta empresa
            </Alert>
          ) : (
            <TelaOrdenacaoList
              telas={telasEmpresa}
              tipoSistema={tipoSistema}
              apenasGlobais={false}
              titulo="Ordenação de Telas - Empresa/Grupo"
              onReordenar={async (telas) => {
                await reordenarMutation.mutateAsync(telas)
              }}
            />
          )}
        </TabPanel>
      </Paper>
    </Box>
  )
}

export default TelasPageEmpresa

